import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, ScanLine, Smartphone, Sparkles, Zap, Gift } from "lucide-react";
import { api } from "../lib/api";
import { BrandTile } from "../components/Brand";
import { CategoryIcon } from "../components/CategoryIcon";
import VoucherCard from "../components/VoucherCard";

export default function ShopHome() {
    const [banners, setBanners] = useState([]);
    const [categories, setCategories] = useState([]);
    const [collections, setCollections] = useState([]);
    const [trending, setTrending] = useState([]);
    const [brands, setBrands] = useState([]);

    useEffect(() => {
        api.get("/api/banners").then((r) => setBanners(r.data.banners));
        api.get("/api/brands/categories").then((r) => setCategories(r.data.categories));
        api.get("/api/collections").then((r) => setCollections(r.data.collections));
        api.get("/api/rewards?featured=true&limit=18&sort=discount").then((r) => setTrending(r.data.rewards));
        api.get("/api/brands?featured=true&limit=24").then((r) => setBrands(r.data.brands));
    }, []);

    return (
        <div className="bg-white">
            <HeroCarousel banners={banners} />
            <TrustStrip />
            <CategoriesRail categories={categories} />
            <BudgetCurations />
            <Section title="Trending now" subtitle="Most coveted this week" items={trending} viewAll="/category/all" />
            {collections.slice(0, 3).map((c) => (
                <Section key={c.slug} title={c.title} subtitle={c.subtitle} items={c.rewards}
                    viewAll={`/category/${encodeURIComponent(c.rewards[0]?.category || "all")}`} />
            ))}
            <BrandsGrid brands={brands} />
            {collections.slice(3).map((c) => (
                <Section key={c.slug} title={c.title} subtitle={c.subtitle} items={c.rewards}
                    viewAll={`/category/${encodeURIComponent(c.rewards[0]?.category || "all")}`} />
            ))}
            <WhatsappStrip />
        </div>
    );
}

// === HERO ===
function HeroCarousel({ banners }) {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (banners.length < 2) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 6500);
        return () => clearInterval(t);
    }, [banners.length]);
    if (!banners.length) return <div className="h-[300px] md:h-[420px] bg-[#f9fafb] animate-pulse" />;
    const b = banners[idx];
    const dark = b.bg_from === "#0B1437" || b.bg_from === "#0b1437";
    return (
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 pt-5">
            <div className="relative rounded-[28px] overflow-hidden h-[300px] md:h-[420px] flex items-center"
                style={{ background: `linear-gradient(135deg, ${b.bg_from}, ${b.bg_to})` }}>
                <div className="absolute inset-0 fx-dot-pattern opacity-30" />
                <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-3xl opacity-40" style={{ background: b.accent }} />
                <div className={`relative px-6 md:px-14 max-w-2xl ${dark ? "text-white" : "text-[#0b1437]"}`}>
                    <span className="fx-chip text-[11px] mb-3" style={{ background: `${b.accent}25`, color: dark ? b.accent : "#0b1437", borderColor: `${b.accent}80`, borderWidth: 1, borderStyle: "solid" }}>
                        ✦ {b.tag || "Featured"}
                    </span>
                    <h1 className="font-display text-4xl md:text-6xl font-extrabold mt-3 leading-[0.95]">{b.title}</h1>
                    <p className={`mt-3 text-base md:text-lg max-w-md ${dark ? "text-white/80" : "text-[#374151]"}`}>{b.subtitle}</p>
                    {b.cta_link?.startsWith("http") ? (
                        <a href={b.cta_link} target="_blank" rel="noreferrer" className="fx-btn-gold mt-6 inline-flex">
                            {b.cta_label} <ArrowRight size={16} />
                        </a>
                    ) : (
                        <Link to={b.cta_link} className={`${dark ? "fx-btn-gold" : "fx-btn-primary"} mt-6 inline-flex`} data-testid="banner-cta">
                            {b.cta_label} <ArrowRight size={16} />
                        </Link>
                    )}
                </div>
                <div className="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-1.5">
                    {banners.map((_, i) => (
                        <button key={i} onClick={() => setIdx(i)}
                            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#0b1437]" : "w-1.5 bg-[#0b1437]/30"}`} />
                    ))}
                </div>
            </div>
        </section>
    );
}

// === TRUST STRIP ===
function TrustStrip() {
    const items = [
        { icon: Zap, label: "10s Delivery", note: "Instant via WhatsApp" },
        { icon: Sparkles, label: "270+ Brands", note: "Curated selection" },
        { icon: Gift, label: "100% Genuine", note: "Verified at source" },
        { icon: ScanLine, label: "AI Bill Scan", note: "Earn on every shop" },
    ];
    return (
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {items.map(({ icon: Icon, label, note }) => (
                    <div key={label} className="fx-card p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#c9a959]/15 text-[#c9a959] flex items-center justify-center"><Icon size={18} /></div>
                        <div>
                            <p className="font-semibold text-[#0b1437] text-sm">{label}</p>
                            <p className="text-[11px] text-[#6b7280]">{note}</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// === CATEGORIES ===
function CategoriesRail({ categories }) {
    if (!categories.length) return null;
    return (
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-12">
            <div className="flex items-end justify-between mb-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#c9a959] mb-1">The Marketplace</p>
                    <h2 className="font-display text-2xl md:text-3xl font-bold">Shop by Category</h2>
                </div>
                <Link to="/category/all" className="text-sm font-semibold text-[#0b1437] hover:text-[#c9a959]">View all</Link>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {categories.slice(0, 12).map((c) => (
                    <Link key={c.name} to={`/category/${encodeURIComponent(c.name)}`} data-testid={`home-cat-${c.name}`}
                        className="fx-card fx-card-hover p-4 text-center group">
                        <div className="flex justify-center mb-2">
                            <CategoryIcon name={c.name} size={20} />
                        </div>
                        <p className="text-sm font-semibold text-[#0b1437] leading-tight line-clamp-2">{c.name}</p>
                        <p className="text-[11px] text-[#6b7280] mt-0.5">{c.count} brands</p>
                    </Link>
                ))}
            </div>
        </section>
    );
}

// === BUDGET CURATIONS ===
function BudgetCurations() {
    const items = [
        { title: "Small thank-yous", value: "Under ₹500", to: "/category/all?max_price=500", from: "#FFF7E6", to_: "#FFE9B8", accent: "#c9a959" },
        { title: "Everyday delights", value: "Under ₹2,000", to: "/category/all?max_price=2000", from: "#EEF6FF", to_: "#D7E8FF", accent: "#0b1437" },
        { title: "Statement gifts", value: "Premium ₹5,000+", to: "/category/all?min_price=5000", from: "#FFE9F2", to_: "#FDD5E6", accent: "#e11d48" },
    ];
    return (
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-12">
            <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#c9a959] mb-1">Curated by budget</p>
                <h2 className="font-display text-2xl md:text-3xl font-bold mb-5">For every occasion</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
                {items.map((b) => (
                    <Link key={b.title} to={b.to} className="rounded-3xl p-7 relative overflow-hidden group transition-transform hover:-translate-y-0.5"
                        style={{ background: `linear-gradient(135deg, ${b.from}, ${b.to_})` }}>
                        <p className="text-sm text-[#374151]">{b.title}</p>
                        <h3 className="font-display text-2xl font-extrabold mt-1 text-[#0b1437]">{b.value}</h3>
                        <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold" style={{ color: b.accent }}>
                            Explore <ArrowRight size={14} />
                        </span>
                        <div className="absolute -bottom-8 -right-6 w-32 h-32 rounded-full blur-2xl opacity-30" style={{ background: b.accent }} />
                    </Link>
                ))}
            </div>
        </section>
    );
}

// === SECTION ===
function Section({ title, subtitle, items, viewAll }) {
    const ref = useRef(null);
    if (!items?.length) return null;
    const scroll = (dx) => ref.current?.scrollBy({ left: dx, behavior: "smooth" });
    return (
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-12">
            <div className="flex items-end justify-between gap-3 mb-4">
                <div>
                    {subtitle && <p className="text-xs uppercase tracking-[0.25em] text-[#c9a959] mb-1">{subtitle}</p>}
                    <h2 className="font-display text-2xl md:text-3xl font-bold">{title}</h2>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => scroll(-400)} className="hidden md:flex w-9 h-9 rounded-full bg-white border border-[rgba(11,20,55,0.10)] items-center justify-center hover:bg-[#f9fafb]"><ChevronLeft size={16} /></button>
                    <button onClick={() => scroll(400)} className="hidden md:flex w-9 h-9 rounded-full bg-white border border-[rgba(11,20,55,0.10)] items-center justify-center hover:bg-[#f9fafb]"><ChevronRight size={16} /></button>
                    {viewAll && <Link to={viewAll} className="text-sm font-semibold text-[#0b1437] hover:text-[#c9a959] flex items-center gap-1">Browse all <ArrowRight size={12} /></Link>}
                </div>
            </div>
            <div ref={ref} className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
                {items.map((r) => (
                    <div key={r.id} className="shrink-0 w-[170px] md:w-[220px]"><VoucherCard r={r} compact /></div>
                ))}
            </div>
        </section>
    );
}

// === BRANDS GRID ===
function BrandsGrid({ brands }) {
    if (!brands.length) return null;
    return (
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-14">
            <div className="flex items-end justify-between mb-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#c9a959] mb-1">Timeless favourites</p>
                    <h2 className="font-display text-2xl md:text-3xl font-bold">Curator's brands</h2>
                </div>
                <Link to="/category/all" className="text-sm font-semibold text-[#0b1437] hover:text-[#c9a959]">All brands</Link>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 gap-3">
                {brands.map((b) => (
                    <Link to={`/brand/${b.slug}`} key={b.id} data-testid={`home-brand-${b.name}`}
                        className="aspect-square rounded-2xl overflow-hidden bg-white border border-[rgba(11,20,55,0.06)] hover:scale-105 hover:border-[#c9a959] transition-all shadow-sm">
                        <BrandTile url={b.logo_url} fallback={b.fallback_logo_url} name={b.name} />
                    </Link>
                ))}
            </div>
        </section>
    );
}

// === WHATSAPP STRIP ===
function WhatsappStrip() {
    return (
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mt-14">
            <div className="rounded-3xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center"
                style={{ background: "linear-gradient(135deg, #E6FBEE 0%, #BFE9D0 100%)" }}>
                <div>
                    <span className="fx-chip bg-white text-[#16A34A] border border-[#16A34A]/40">
                        <Smartphone size={12} /> WhatsApp engine
                    </span>
                    <h3 className="font-display text-3xl md:text-4xl font-bold mt-3 text-[#0b1437]">Loyalty that lives in your DMs.</h3>
                    <p className="text-[#374151] mt-3">
                        Send <span className="text-[#0b1437] font-semibold">"Hi"</span> to{" "}
                        <span className="text-[#0b1437] font-semibold">+91 80729 05306</span>. Instant wallet, bill scans, redemptions — all in chat.
                    </p>
                    <a href="https://wa.me/918072905306?text=Hi" target="_blank" rel="noreferrer" className="fx-btn-primary mt-5 inline-flex">
                        Chat now <ArrowRight size={16} />
                    </a>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                    {["1. Upload Bill", "2. My Wallet", "3. Rewards", "4. Gift Cards", "5. Offers", "6. Shop", "7. Transactions", "8. Refer & Earn"].map((x) => (
                        <span key={x} className="px-3 py-2 rounded-xl bg-white border border-[#16A34A]/20 text-[#0b1437] font-medium">{x}</span>
                    ))}
                </div>
            </div>
        </section>
    );
}
